/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import jsQR from "jsqr";
import { ArrowLeft, ScanLine, CheckCircle2, XCircle, AlertCircle, Camera, Type, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { billetterieApi, formatApiError } from "@/lib/api";

export default function AdminBilletterieScan() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const lastScanRef = useRef({ code: "", at: 0 });
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const [cameraError, setCameraError] = useState("");

  const startCamera = async () => {
    setCameraError(""); setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setScanning(true);
        tick();
      }
    } catch (e) {
      setCameraError("Impossible d'accéder à la caméra. Vérifiez les permissions du navigateur.");
    }
  };

  const stopCamera = () => {
    cancelAnimationFrame(animationRef.current);
    const stream = videoRef.current?.srcObject;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setScanning(false);
  };

  useEffect(() => () => stopCamera(), []);

  const tick = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationRef.current = requestAnimationFrame(tick);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const qr = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
    if (qr && qr.data) {
      handleScan(qr.data);
    }
    animationRef.current = requestAnimationFrame(tick);
  };

  const handleScan = async (raw) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code) return;
    // Debounce same code
    const now = Date.now();
    if (lastScanRef.current.code === code && now - lastScanRef.current.at < 3000) return;
    lastScanRef.current = { code, at: now };
    try {
      const res = await billetterieApi.adminScan(code);
      setResult(res);
      if (res.status === "ok") {
        toast.success(`Entrée validée : ${res.ticket?.ticket_type_name}`);
        try { navigator.vibrate?.(50); } catch {}
      } else if (res.status === "already_used") {
        toast.error(`Billet déjà utilisé`);
      } else if (res.status === "not_found") {
        toast.error("Billet inconnu");
      } else if (res.status === "not_paid") {
        toast.error("Billet non payé");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitManual = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    handleScan(manualCode);
    setManualCode("");
  };

  const reset = () => setResult(null);

  return (
    <div className="min-h-screen" data-testid="admin-bill-scan">
      <header className="mb-6">
        <Link to="/admin/billetterie" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-[#DCB457]"><ArrowLeft size={11} /> Retour</Link>
        <h1 className="font-serif-luxe text-3xl md:text-4xl mt-3">Scanner <span className="italic gold-text">d'entrée.</span></h1>
        <p className="text-white/55 text-sm mt-2 max-w-2xl">
          Pointez la caméra vers le QR code du billet ou saisissez le code à 12 caractères. Le scanner fonctionne sur smartphone (PWA).
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-luxe p-5">
          <p className="eyebrow mb-3">Caméra</p>
          <div className="relative bg-black aspect-square rounded overflow-hidden">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            <canvas ref={canvasRef} className="hidden" />
            {!scanning && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                <button onClick={startCamera} className="btn-gold inline-flex items-center gap-2"><Camera size={16} /> Activer la caméra</button>
              </div>
            )}
            {scanning && (
              <>
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-12 border-2 border-[#DCB457] rounded" />
                </div>
                <button onClick={stopCamera} className="absolute bottom-3 right-3 bg-black/70 text-white border border-white/30 px-3 py-1.5 text-xs uppercase tracking-[0.18em]">Stopper</button>
              </>
            )}
          </div>
          {cameraError && <p className="mt-3 text-xs text-red-300 inline-flex items-center gap-1.5"><AlertCircle size={12} /> {cameraError}</p>}

          <form onSubmit={submitManual} className="mt-5">
            <p className="eyebrow mb-2">Saisie manuelle</p>
            <div className="flex gap-2">
              <input className="input-luxe flex-1 font-mono uppercase tracking-[0.18em]" placeholder="ABC123XYZ456" value={manualCode} onChange={(e) => setManualCode(e.target.value.toUpperCase())} data-testid="scan-manual-input" />
              <button type="submit" className="btn-gold inline-flex items-center gap-1"><Type size={14} /> OK</button>
            </div>
          </form>
        </div>

        <div className="card-luxe p-5" data-testid="scan-result">
          <div className="flex items-center justify-between">
            <p className="eyebrow">Résultat</p>
            {result && <button onClick={reset} className="text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-[#DCB457] inline-flex items-center gap-1"><RefreshCcw size={11} /> Réinitialiser</button>}
          </div>

          {!result ? (
            <div className="mt-6 text-center py-12">
              <ScanLine size={48} strokeWidth={1} className="text-white/30 mx-auto mb-4" />
              <p className="text-white/55 text-sm">En attente d'un scan…</p>
            </div>
          ) : result.status === "ok" ? (
            <div className="mt-6 p-6 border border-emerald-400/40 bg-emerald-500/8 rounded">
              <CheckCircle2 size={48} strokeWidth={1.5} className="text-emerald-300 mb-3" />
              <h2 className="font-serif-luxe text-3xl text-emerald-300">Entrée validée</h2>
              <p className="text-white/85 mt-3 text-sm">
                <strong>{result.ticket.ticket_type_name}</strong> — {result.ticket.buyer_name}
              </p>
              <p className="text-white/55 text-xs font-mono mt-2">{result.ticket.code}</p>
            </div>
          ) : result.status === "already_used" ? (
            <div className="mt-6 p-6 border border-amber-400/40 bg-amber-500/8 rounded">
              <AlertCircle size={48} strokeWidth={1.5} className="text-amber-300 mb-3" />
              <h2 className="font-serif-luxe text-3xl text-amber-300">Déjà utilisé</h2>
              <p className="text-white/85 mt-3 text-sm">
                <strong>{result.ticket.ticket_type_name}</strong> — {result.ticket.buyer_name}
              </p>
              <p className="text-white/55 text-xs mt-2">Utilisé le {new Date(result.used_at).toLocaleString("fr-FR")}</p>
              <p className="text-white/55 text-xs">par {result.used_by}</p>
            </div>
          ) : (
            <div className="mt-6 p-6 border border-red-400/40 bg-red-500/8 rounded">
              <XCircle size={48} strokeWidth={1.5} className="text-red-300 mb-3" />
              <h2 className="font-serif-luxe text-3xl text-red-300">
                {result.status === "not_found" ? "Billet inconnu" : "Billet non valide"}
              </h2>
              <p className="text-white/55 text-xs mt-2">Code scanné : <span className="font-mono">{result.code || result.ticket?.code}</span></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
